import { useState, useEffect } from "react";
import { Modal, Form, Alert } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  ServerRequestsParkerIntegrationPartyDto,
  ParkerIntegrationPartyApi,
} from "./../shared/api/axios-client";
import { Button } from "./Button";
import { createApiService } from "../shared/apiConfig";

interface EditRfidModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  integration: ServerRequestsParkerIntegrationPartyDto;
  subscriptionId: number;
  parkerId: number;
  facilityId: number;
  onSuccess: (updated: ServerRequestsParkerIntegrationPartyDto) => void;
}

export function EditRfidModal({
  show,
  setShow,
  integration,
  subscriptionId,
  parkerId,
  facilityId,
  onSuccess,
}: EditRfidModalProps) {
  const maxLength = integration.rfidValidationMaxLength ?? 20;
  const validationPattern = integration.rfidValidationPattern;
  const initialValue = integration.metaFlashRFIDNumber ?? "";
  const [value, setValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const hasChanged = value !== initialValue;

  useEffect(() => {
    if (show) {
      setValue(integration.metaFlashRFIDNumber ?? "");
      setError(null);
    }
  }, [show, integration.metaFlashRFIDNumber]);

  function validate(): boolean {
    if (!value.trim()) {
      setError("RFID is required");
      return false;
    }
    if (value.length > maxLength) {
      setError(`RFID must be ${maxLength} characters or less`);
      return false;
    }
    if (validationPattern) {
      try {
        const regex = new RegExp(validationPattern);
        if (!regex.test(value)) {
          setError("RFID format is invalid");
          return false;
        }
      } catch {
        setError("Validation error");
        return false;
      }
    }
    setError(null);
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      const api = createApiService(ParkerIntegrationPartyApi);
      const response = await api.parkerIntegrationPartiesCredentialUpdatePatch({
        serverRequestsUpdateCredentialRequest: {
          facilityId,
          subscriptionId,
          parkerId,
          integrationPartyId: integration.integrationPartyId!,
          updatedCredential: value.trim(),
        },
      });

      if (response.data.success && response.data.parkerIntegrationParty) {
        onSuccess(response.data.parkerIntegrationParty);
        setShow(false);
        toast.success("RFID updated successfully");
      } else {
        toast.error("Failed to update RFID");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? "Failed to update RFID");
    } finally {
      setSubmitting(false);
    }
  }

  if (!show) return null;

  return (
    <Modal show centered onHide={() => setShow(false)}>
      <Modal.Header closeButton>
        <Modal.Title>Edit RFID / Scanner Code</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          You can override the generated code with the RFID number from a physical pass.  
          <b> The old number will not scan at the gate after saving.</b> The parker will receive an email confirmation of the change.
        </Alert>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>
              RFID for {integration.integrationName}
            </Form.Label>
            <Form.Control
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              maxLength={maxLength}
              isInvalid={!!error}
            />
            <Form.Text className="text-muted">
              Maximum {maxLength} characters
            </Form.Text>
            {error && (
              <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
            )}
          </Form.Group>
          <div className="d-flex justify-content-end gap-2">
            <Button
              type="button"
              className="btn-outline-secondary"
              onClick={() => setShow(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn-main"
              disabled={submitting || !hasChanged}
            >
              {submitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
